import React, { CSSProperties, ReactNode, RefObject, useContext, useEffect, useId, useRef, useState } from 'react';
import CursorProvider, { CursorState } from '../../../providers/CursorProvider';
import Vector2 from '../../../utility/Vector2';

export enum TargetBehavior {
    None,
    Stick,
    Reset
}

interface FreeDragProps {
    id ?: string;
    x ?: number;
    y ?: number;
    w ?: string;
    h ?: string;
    children ?: ReactNode;
    wrapper?: HTMLElement;
    sourceId?: string;
    dragRef?: RefObject<HTMLDivElement>;
    posRef?: RefObject<Vector2>;
    stickToSource?: boolean;
    onDragged?: (x: number, y: number) => void;
    onDragEnded?: (r : RefObject<HTMLDivElement>) => void;
    targetId?: string;
    canReachTarget ?: () => boolean;
    targetBehaviour ?: TargetBehavior;
    targetScale ?: number;
    onTarget?: () => void;
}

export function FreeDrag(props: FreeDragProps) {
    const cursorProvider = useContext(CursorProvider.context);
    let animationFrame: number;

    // x, y is the initial position of the draggable element (and pos it resets to)
    // pos is the offset from the source object.
    const x : number = props.x !== undefined ? props.x : 0;
    const y : number = props.y !== undefined ? props.y : 0;
    const w : string|undefined = props.w;
    const h : string|undefined = props.h;
    const [pos, setPos] = useState(new Vector2(x, y));
    useEffect(() => {
        if (props.posRef !== undefined)
            props.posRef.current = pos;
    }, [pos]);

    // create an id for this drag & give all the child elements this id.
    const id = useRef<string>(props.id !== undefined ? props.id : useId());
    let children : ReactNode = props.children;
    children = React.Children.map(children, (child : ReactNode) => {
        if (React.isValidElement(child))
        {
            const element = child as any;
            return React.cloneElement(element, { id: props.id });
        }
        return child;
    });

    // use this id to detect the screen pos & bounds of this draggable
    const selfRect = useRef<DOMRect>(DOMRect.prototype);
    const dragRef = props.dragRef !== undefined ? props.dragRef : useRef<HTMLDivElement>(HTMLDivElement.prototype);
    const ogRect = useRef<DOMRect>(DOMRect.prototype);
    const ogScroll = useRef<Vector2>(new Vector2(0, 0));
    const refreshRect = () => {
        const selfElement = document.getElementById(id.current);
        if (selfElement !== null)
            selfRect.current = selfElement.getBoundingClientRect();
    }

    // tracking the drag state.
    const dragging = useRef(false);
    const hasDragged = useRef(false);
    const relDrag = useRef<Vector2>(new Vector2(0, 0));
    
    // tracking the cursor position.
    const cursorPos = useRef<Vector2>(new Vector2(0, 0));
    const cursorDelta = useRef<Vector2>(new Vector2(0, 0));
    const cursorState = useRef(cursorProvider.state);
    
    // tracking the sticky states.
    const stickSource = useRef(props.stickToSource !== undefined && props.stickToSource);
    const stickTarget = useRef(false);
    
    useEffect(() => {
        cursorState.current = cursorProvider.state;
    }, [cursorProvider.state]);

    const mouseWithinRange = () : boolean => {  
        // mouse is in screen space, so we can just test against screen space selfRect      
        let diffX = cursorPos.current.x - selfRect.current.x;
        let diffY = cursorPos.current.y - selfRect.current.y;
        return diffX >= 0 && diffX <= selfRect.current.width && diffY >= 0 && diffY <= selfRect.current.height;
    };

    const isFocused = () : boolean => {
        return document.elementFromPoint(cursorPos.current.x, cursorPos.current.y)?.id == id.current;
    }

    const atTarget = () : boolean => {
        if (props.targetId === undefined)
            return false;
        const target = document.getElementById(props.targetId);
        if (target === undefined)
            return false;
        var targetRect = target?.getBoundingClientRect();
        if (targetRect === undefined)
            return false;
        return ((selfRect.current.left >= targetRect.left && selfRect.current.left <= targetRect.right)
            || (selfRect.current.right >= targetRect.left && selfRect.current.right <= targetRect.right)
            || (selfRect.current.left <= targetRect.left && selfRect.current.right >= targetRect.right))
            && ((selfRect.current.bottom >= targetRect.top && selfRect.current.bottom <= targetRect.bottom)
            || (selfRect.current.top >= targetRect.top && selfRect.current.top <= targetRect.bottom)
            || (selfRect.current.top <= targetRect.top && selfRect.current.bottom >= targetRect.bottom));
    }

    const onMouseOver = (e: MouseEvent) => {
        refreshRect();
        if (mouseWithinRange() && isFocused())
            e.preventDefault();
    }

    const onMouseMove = (e: MouseEvent) => {
        let cursorX = 0;
        let cursorY = 0;
        
        if (props.wrapper) {
            const boundingRect = props.wrapper.getBoundingClientRect();
            cursorX = e.clientX - boundingRect.left;
            cursorY = e.clientY - boundingRect.top;
        } else {
            cursorX = e.clientX;
            cursorY = e.clientY;
        }

        cursorDelta.current.x = cursorX - cursorPos.current.x;
        cursorDelta.current.y = cursorY - cursorPos.current.y;
        cursorPos.current.x = cursorX;
        cursorPos.current.y = cursorY;

        if (dragging.current)
        {
            refreshRect();
            let dragX = cursorPos.current.x - relDrag.current.x - ogRect.current.left + x + (window.scrollX - ogScroll.current.x);
            let dragY = cursorPos.current.y - relDrag.current.y - ogRect.current.top + y + (window.scrollY - ogScroll.current.y);
            setPos(new Vector2(dragX, dragY));
            if (props.onDragged !== undefined)
                props.onDragged(dragX, dragY);
            return;
        }
    };

    const onMouseDown = (e: MouseEvent) => {
        if (cursorState.current != CursorState.YOY)
            return;
        refreshRect();
        if (mouseWithinRange() && isFocused())
        {
            e.preventDefault();
            dragging.current = true;
            hasDragged.current = true;

            relDrag.current.x = cursorPos.current.x - selfRect.current.x;
            relDrag.current.y = cursorPos.current.y - selfRect.current.y;
        }
    };

    const onUpdate = () => { };

    const onMouseUp = (e: MouseEvent) => {
        if (dragging.current)
        {
            e.preventDefault();
            dragging.current = false;
            
            refreshRect();

            if (props.targetId !== undefined)
            {
                const isAtTarget = atTarget();
                if (isAtTarget)
                {
                    const canReachTarget = props.canReachTarget !== undefined ? props.canReachTarget() : true;
                    if (canReachTarget)
                    {
                        const targetBehavior = props.targetBehaviour !== undefined ? props.targetBehaviour : TargetBehavior.None;
                        switch(targetBehavior)
                        {
                            case TargetBehavior.Stick:
                                const targetRect = document.getElementById(props.targetId)?.getBoundingClientRect();
                                if (targetRect !== undefined)
                                {
                                    stickTarget.current = true;
                                    setPos(new Vector2(
                                        targetRect.x - ogRect.current.x + window.scrollX - ogScroll.current.x,
                                        targetRect.y - ogRect.current.y + window.scrollY - ogScroll.current.y
                                    ));
                                }
                                break;
                            case TargetBehavior.Reset:
                                setPos(new Vector2(x, y));
                                break;
                        }
                                             
                        if (props.onTarget !== undefined)
                            props.onTarget();
                    }
                }
            }

            if (props.onDragEnded !== undefined)
                props.onDragEnded(dragRef);

            if (stickSource.current)
                setPos(new Vector2(x, y));
        }
    };

    const consumeDrag = (e: DragEvent) => {
        e.preventDefault();
    }

    const init = () => {
        var selfElement = document.getElementById(id.current);
        if (selfElement !== null)
            ogRect.current = selfElement.getBoundingClientRect();
        ogScroll.current.x = window.scrollX;
        ogScroll.current.y = window.scrollY;

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("mouseup", onMouseUp);
        window.addEventListener("mouseover", onMouseOver);
        window.addEventListener("dragstart", consumeDrag);
        window.addEventListener("dragover", consumeDrag);
        window.addEventListener("dragenter", consumeDrag);
        window.addEventListener("dragend", consumeDrag);
        window.addEventListener("dragleave", consumeDrag);

        loop();
    }

    const loop = () => {
        onUpdate();
        animationFrame = requestAnimationFrame(loop);
    };


    useEffect(() => {
        init();
        return () => {
            cancelAnimationFrame(animationFrame);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("mouseup", onMouseUp);
            window.removeEventListener("mouseover", onMouseOver);
            window.removeEventListener("dragstart", consumeDrag);
            window.removeEventListener("dragover", consumeDrag);
            window.removeEventListener("dragenter", consumeDrag);
            window.removeEventListener("dragend", consumeDrag);
            window.removeEventListener("dragleave", consumeDrag);
        }
    }, [props]);

    let style : CSSProperties = { top: pos.y, left: pos.x };
    if (w !== undefined && h !== undefined)
        style = { top: pos.y, left: pos.x, width: w, height: h};
    else if (w !== undefined)
        style = { top: pos.y, left: pos.x, width: w};
    else if (h !== undefined)
        style = { top: pos.y, left: pos.x, height: h};

    return <div
        id={id.current}
        ref={dragRef}
        className='draggable'
        style={style}>
            {children}
    </div>
}